// Editor dos tributos de um item. Mostra a sugestão e deixa o usuário ajustar.
import React from "react";
import { Campo, Entrada, Numero, Selecao } from "./ui";

const CSOSN = [["101", "101 — Com permissão de crédito"], ["102", "102 — Sem permissão de crédito"],
  ["103", "103 — Isenção (faixa de receita)"], ["201", "201 — Com crédito e ST retida pelo emitente"],
  ["202", "202 — Sem crédito e ST retida pelo emitente"], ["300", "300 — Imune"], ["400", "400 — Não tributada"],
  ["500", "500 — ICMS cobrado anteriormente por ST"], ["900", "900 — Outros"]];
const CST_ICMS = [["00", "00 — Tributada integralmente"], ["10", "10 — Tributada com ST retida pelo emitente"],
  ["20", "20 — Com redução de base"], ["40", "40 — Isenta"], ["41", "41 — Não tributada"], ["50", "50 — Suspensão"],
  ["51", "51 — Diferimento"], ["60", "60 — Cobrado anteriormente por ST"], ["90", "90 — Outras"]];
const CST_PC = [["01", "01 — Tributável alíquota básica"], ["04", "04 — Monofásica (revenda a alíquota zero)"],
  ["06", "06 — Alíquota zero"], ["07", "07 — Isenta"], ["08", "08 — Sem incidência"], ["09", "09 — Suspensão"],
  ["49", "49 — Outras operações de saída"], ["99", "99 — Outras operações"]];
const ORIGENS = [[0, "0 — Nacional"], [1, "1 — Estrangeira, importação direta"], [2, "2 — Estrangeira, mercado interno"],
  [3, "3 — Nacional, conteúdo importado > 40%"], [4, "4 — Nacional, processos básicos"],
  [5, "5 — Nacional, conteúdo importado ≤ 40%"], [6, "6 — Estrangeira sem similar (CAMEX)"],
  [7, "7 — Estrangeira mercado interno sem similar"], [8, "8 — Nacional, conteúdo importado > 70%"]];

export default function Tributos({ valor, onChange, regime }) {
  const t = valor || {};
  const set = (campo, v) => onChange({ ...t, [campo]: v });
  const simples = ["1", "2", "4"].includes(String(regime));
  const comST = t.icms_cst === "10" || ["201", "202"].includes(t.icms_csosn);
  const pct = (campo) => (
    <Numero value={t[campo] ?? 0} onChange={(v) => set(campo, Number(v))} />
  );

  return (
    <div className="space-y-3 rounded-md border border-blue-100 bg-blue-50/40 p-4">
      {t.observacao && <p className="text-xs text-blue-900">Sugestão: {t.observacao}</p>}
      {(t.avisos || []).map((a) => (
        <p key={a} className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">⚠ {a}</p>
      ))}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Campo rotulo="CFOP">
          <Entrada value={t.cfop || ""} maxLength={4} onChange={(e) => set("cfop", e.target.value.replace(/\D/g, ""))} />
        </Campo>
        <Campo rotulo="Origem" className="md:col-span-3">
          <Selecao value={t.origem ?? 0} onChange={(e) => set("origem", Number(e.target.value))} opcoes={ORIGENS} />
        </Campo>

        {simples ? (
          <Campo rotulo="CSOSN (ICMS)" className="col-span-2">
            <Selecao value={t.icms_csosn || "102"} onChange={(e) => set("icms_csosn", e.target.value)} opcoes={CSOSN} />
          </Campo>
        ) : (
          <Campo rotulo="CST ICMS" className="col-span-2">
            <Selecao value={t.icms_cst || "00"} onChange={(e) => set("icms_cst", e.target.value)} opcoes={CST_ICMS} />
          </Campo>
        )}
        {["101", "201"].includes(t.icms_csosn) && <Campo rotulo="% crédito SN">{pct("credito_sn_aliquota")}</Campo>}
        {!simples && <Campo rotulo="% ICMS">{pct("icms_aliquota")}</Campo>}
        {!simples && t.icms_cst === "20" && <Campo rotulo="% redução da base">{pct("icms_reducao_base")}</Campo>}
        {!simples && <Campo rotulo="% FCP">{pct("fcp_aliquota")}</Campo>}
        <Campo rotulo="cBenef" dica="Código de benefício fiscal da UF">
          <Entrada value={t.cbenef || ""} maxLength={10} onChange={(e) => set("cbenef", e.target.value.toUpperCase() || null)} />
        </Campo>
      </div>

      {comST && (
        <div className="grid grid-cols-2 gap-3 rounded border border-blue-100 bg-white/60 p-3 md:grid-cols-5">
          <p className="col-span-2 text-xs font-semibold text-gray-700 md:col-span-5">Substituição tributária (retida por esta nota)</p>
          {t.st_base_dupla ? (
            <p className="col-span-2 text-xs text-gray-600">DIFAL por ST em uso e consumo (base dupla, sem MVA)</p>
          ) : (
            <>
              <Campo rotulo="% MVA">{pct("icms_mva_st")}</Campo>
              <Campo rotulo="MVA ajustada?">
                <Selecao value={t.st_mva_ajustada ? "1" : "0"} onChange={(e) => set("st_mva_ajustada", e.target.value === "1")}
                  opcoes={[["1", "Sim (interestadual)"], ["0", "Não"]]} />
              </Campo>
            </>
          )}
          <Campo rotulo="% ICMS ST (destino)">{pct("icms_aliquota_st")}</Campo>
          <Campo rotulo="% redução base ST">{pct("icms_reducao_base_st")}</Campo>
          <Campo rotulo="% FCP ST">{pct("fcp_st_aliquota")}</Campo>
        </div>
      )}

      {t.icms_aliquota_destino != null && !comST && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Campo rotulo="% interna no destino (DIFAL)">{pct("icms_aliquota_destino")}</Campo>
          <Campo rotulo="% FCP no destino">{pct("fcp_aliquota_destino")}</Campo>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Campo rotulo="CST PIS" className="col-span-2">
          <Selecao value={t.pis_cst || "49"} onChange={(e) => set("pis_cst", e.target.value)} opcoes={CST_PC} />
        </Campo>
        <Campo rotulo="% PIS">{pct("pis_aliquota")}</Campo>
        <div />
        <Campo rotulo="CST COFINS" className="col-span-2">
          <Selecao value={t.cofins_cst || "49"} onChange={(e) => set("cofins_cst", e.target.value)} opcoes={CST_PC} />
        </Campo>
        <Campo rotulo="% COFINS">{pct("cofins_aliquota")}</Campo>
        <div />
        <Campo rotulo="CST IBS/CBS" dica="Reforma tributária">
          <Entrada value={t.ibs_cbs_cst || "000"} maxLength={3} onChange={(e) => set("ibs_cbs_cst", e.target.value.replace(/\D/g, ""))} />
        </Campo>
        <Campo rotulo="cClassTrib">
          <Entrada value={t.ibs_cbs_class_trib || "000001"} maxLength={6} onChange={(e) => set("ibs_cbs_class_trib", e.target.value.replace(/\D/g, ""))} />
        </Campo>
      </div>
    </div>
  );
}
